import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Filter, X, Clock, Star, Truck, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";
import { apiClient } from "@/lib/api";
import CategoryPills from "@/components/CategoryPills";
import RestaurantCard from "@/components/RestaurantCard";
import { formatGNF } from "@/utils/format";

const DELIVERY_TIMES = [15, 25, 45, 60];
const MIN_RATINGS = [4.5, 4.0, 3.5];
const MAX_FEES = [10000, 15000, 25000];

export default function Restaurants() {
  const { t, i18n } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [restaurants, setRestaurants] = useState([]);
  const [zones, setZones] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);

  const category = params.get("category") || null;
  const zone = params.get("zone") || "";
  const q = params.get("q") || "";
  const openOnly = params.get("open_only") === "true";
  const noSpicy = params.get("no_spicy") === "true";
  const minRating = params.get("min_rating");
  const maxDeliveryTime = params.get("max_delivery_time");
  const maxDeliveryFee = params.get("max_delivery_fee");
  const sort = params.get("sort") || "";

  const activeFiltersCount =
    (openOnly ? 1 : 0) + (noSpicy ? 1 : 0) + (minRating ? 1 : 0) +
    (maxDeliveryTime ? 1 : 0) + (maxDeliveryFee ? 1 : 0) + (sort ? 1 : 0) + (zone ? 1 : 0);

  useEffect(() => {
    apiClient.get("/zones").then(({ data }) => setZones(data)).catch(() => {});
    apiClient.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (category) qs.append("category", category);
    if (zone) qs.append("zone", zone);
    if (q) qs.append("q", q);
    if (openOnly) qs.append("open_only", "true");
    if (noSpicy) qs.append("no_spicy", "true");
    if (minRating) qs.append("min_rating", minRating);
    if (maxDeliveryTime) qs.append("max_delivery_time", maxDeliveryTime);
    if (maxDeliveryFee) qs.append("max_delivery_fee", maxDeliveryFee);
    if (sort) qs.append("sort", sort);
    apiClient.get(`/restaurants?${qs.toString()}`).then(({ data }) => {
      setRestaurants(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [category, zone, q, openOnly, noSpicy, minRating, maxDeliveryTime, maxDeliveryFee, sort, i18n.language]);

  const setParam = (key, val) => {
    const next = new URLSearchParams(params);
    if (val == null || val === "" || val === false) next.delete(key);
    else next.set(key, String(val));
    setParams(next);
  };

  const toggleParam = (key, val) => {
    const current = params.get(key);
    setParam(key, current === String(val) ? "" : val);
  };

  const resetFilters = () => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (category) next.set("category", category);
    setParams(next);
  };

  const catLabel = (key) => cats.find((c) => c.key === key)?.label || (key ? key.charAt(0).toUpperCase() + key.slice(1).replace("_", " ") : key);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.25em] text-brand-red mb-2">{t("home.tagline")}</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold">{t("restaurants.pageTitle")}</h1>
        <p className="text-mute mt-2">{t("restaurants.pageSubtitle")}</p>
      </div>

      {/* Search + filter toggle */}
      <div className="bg-white rounded-2xl border border-sand p-4 mb-4 flex flex-col md:flex-row gap-3">
        <div className="flex-1 flex items-center gap-2 px-3 bg-elevated rounded-xl">
          <Search className="w-4 h-4 text-mute" />
          <input
            type="text"
            data-testid="restaurants-search"
            value={q}
            onChange={(e) => setParam("q", e.target.value)}
            placeholder={t("restaurants.searchPlaceholder")}
            className="bg-transparent outline-none w-full py-3 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="zone-filter"
            value={zone}
            onChange={(e) => setParam("zone", e.target.value)}
            className="bg-elevated rounded-xl px-4 py-3 text-sm outline-none"
          >
            <option value="">{t("restaurants.allZones")}</option>
            {zones.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
          <button
            data-testid="filters-toggle"
            onClick={() => setPanelOpen((v) => !v)}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold border transition-colors ${
              panelOpen || activeFiltersCount > 0
                ? "bg-brand-red text-white border-brand-red"
                : "bg-white text-ink border-sand hover:border-brand-red"
            }`}
          >
            <Filter className="w-4 h-4" />
            {t("restaurants.filters")}
            {activeFiltersCount > 0 && (
              <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold ${panelOpen ? "bg-white text-brand-red" : "bg-brand-red text-white"}`}>
                {activeFiltersCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Filter panel */}
      {panelOpen && (
        <div data-testid="filters-panel" className="bg-white rounded-2xl border border-sand p-5 mb-4 animate-in slide-in-from-top-2 duration-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Filter className="w-4 h-4" /> {t("restaurants.filters")}</h3>
            {activeFiltersCount > 0 && (
              <button onClick={resetFilters} data-testid="filters-reset" className="text-xs text-brand-red inline-flex items-center gap-1 hover:underline">
                <X className="w-3 h-3" /> {t("restaurants.resetFilters")}
              </button>
            )}
          </div>

          {/* Toggles */}
          <div className="flex flex-wrap gap-2 mb-5">
            <FilterChip
              icon={<Zap className="w-3.5 h-3.5" />}
              active={openOnly}
              onClick={() => setParam("open_only", !openOnly)}
              testId="chip-open-only"
              label={t("restaurants.openNow")}
            />
            <FilterChip
              active={noSpicy}
              onClick={() => setParam("no_spicy", !noSpicy)}
              testId="chip-no-spicy"
              label={t("restaurants.noSpicyChip")}
            />
            <FilterChip
              icon={<Clock className="w-3.5 h-3.5" />}
              active={maxDeliveryTime === "25"}
              onClick={() => toggleParam("max_delivery_time", 25)}
              testId="chip-fast-delivery"
              label={t("rl.fastDeliveryChip")}
            />
          </div>

          {/* Min rating */}
          <FilterGroup icon={<Star className="w-4 h-4 fill-[#F4C430] text-[#F4C430]" />} label={t("restaurants.minRating")}>
            {MIN_RATINGS.map((r) => (
              <FilterChip
                key={r}
                active={minRating === String(r)}
                onClick={() => toggleParam("min_rating", r)}
                testId={`chip-min-rating-${r}`}
                label={`≥ ${r} ⭐`}
              />
            ))}
          </FilterGroup>

          {/* Max delivery time */}
          <FilterGroup icon={<Clock className="w-4 h-4 text-brand-red" />} label={t("restaurants.maxDeliveryTime")}>
            {DELIVERY_TIMES.map((m) => (
              <FilterChip
                key={m}
                active={maxDeliveryTime === String(m)}
                onClick={() => toggleParam("max_delivery_time", m)}
                testId={`chip-max-time-${m}`}
                label={`≤ ${m} min`}
              />
            ))}
          </FilterGroup>

          {/* Max delivery fee */}
          <FilterGroup icon={<Truck className="w-4 h-4 text-brand-red" />} label={t("restaurants.maxDeliveryFee")}>
            {MAX_FEES.map((f) => (
              <FilterChip
                key={f}
                active={maxDeliveryFee === String(f)}
                onClick={() => toggleParam("max_delivery_fee", f)}
                testId={`chip-max-fee-${f}`}
                label={`≤ ${formatGNF(f)}`}
              />
            ))}
          </FilterGroup>

          {/* Sort */}
          <FilterGroup label={t("restaurants.sortBy")}>
            <FilterChip active={sort === "rating"} onClick={() => toggleParam("sort", "rating")} testId="chip-sort-rating" label={t("restaurants.sortRating")} />
            <FilterChip active={sort === "delivery_time"} onClick={() => toggleParam("sort", "delivery_time")} testId="chip-sort-time" label={t("restaurants.sortTime")} />
            <FilterChip active={sort === "delivery_fee"} onClick={() => toggleParam("sort", "delivery_fee")} testId="chip-sort-fee" label={t("restaurants.sortFee")} />
          </FilterGroup>
        </div>
      )}

      <CategoryPills value={category} onChange={(c) => setParam("category", c)} />

      {/* Active filters banner — each chip removable in one click */}
      {(activeFiltersCount > 0 || category || q) && (
        <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="active-filters-banner">
          {category && <ActiveChip testId="active-chip-category" label={`${catLabel(category)}`} onRemove={() => setParam("category", null)} />}
          {zone && <ActiveChip testId="active-chip-zone" label={`📍 ${zone}`} onRemove={() => setParam("zone", "")} />}
          {q && <ActiveChip testId="active-chip-q" label={`« ${q} »`} onRemove={() => setParam("q", "")} />}
          {noSpicy && <ActiveChip testId="active-chip-no-spicy" label={t("restaurants.noSpicyChip")} onRemove={() => setParam("no_spicy", false)} />}
          {openOnly && <ActiveChip testId="active-chip-open" label={t("restaurants.openNow")} onRemove={() => setParam("open_only", false)} />}
          {minRating && <ActiveChip testId="active-chip-rating" label={`≥ ${minRating} ⭐`} onRemove={() => setParam("min_rating", null)} />}
          {maxDeliveryTime && <ActiveChip testId="active-chip-time" label={`≤ ${maxDeliveryTime} min`} onRemove={() => setParam("max_delivery_time", null)} />}
          {maxDeliveryFee && <ActiveChip testId="active-chip-fee" label={`≤ ${formatGNF(Number(maxDeliveryFee))}`} onRemove={() => setParam("max_delivery_fee", null)} />}
          {sort && <ActiveChip testId="active-chip-sort" label={t(`restaurants.sort${sort === "rating" ? "Rating" : sort === "delivery_time" ? "Time" : "Fee"}`)} onRemove={() => setParam("sort", null)} />}
        </div>
      )}

      <div className="mt-6">
        <div className="text-xs text-mute mb-3" data-testid="results-count">
          {loading ? "…" : t("restaurants.resultsCount", { count: restaurants.length })}
        </div>
        {loading ? (
          <div className="text-mute">{t("common.loading")}</div>
        ) : restaurants.length === 0 ? (
          <div className="bg-white border border-sand rounded-2xl p-10 text-center" data-testid="no-results-block">
            <div className="text-3xl mb-2">🔍</div>
            <div className="font-display text-lg font-semibold text-ink mb-1" data-testid="no-results-message">
              {t("rl.noRestaurant")}{category ? ` ${catLabel(category)}` : ""}{zone ? ` ${t("rl.inZone")} ${zone}` : ""} {t("rl.forNow")}
            </div>
            <p className="text-sm text-mute mb-4">{t("rl.widenHint")}</p>
            <button
              onClick={() => { if (zone) setParam("zone", ""); else if (category) setParam("category", null); else resetFilters(); }}
              data-testid="widen-search-btn"
              className="rounded-full bg-brand-red text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#C81F2D]"
            >
              {zone ? t("rl.searchAll") : category ? t("rl.allCuisines") : t("rl.resetFilters")}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="restaurants-grid">
            {restaurants.map((r) => <RestaurantCard key={r.restaurant_id} restaurant={r} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function ActiveChip({ label, onRemove, testId }) {
  return (
    <span data-testid={testId} className="inline-flex items-center gap-1.5 rounded-full bg-brand-red/10 text-brand-red border border-brand-red/30 px-3 py-1.5 text-xs font-semibold">
      {label}
      <button type="button" onClick={onRemove} aria-label={`Retirer le filtre ${label}`} className="rounded-full hover:bg-brand-red hover:text-white p-0.5 transition-colors">
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

function FilterGroup({ icon, label, children }) {
  return (
    <div className="mb-4">
      <div className="text-xs uppercase tracking-widest text-mute mb-2 flex items-center gap-1.5">{icon}{label}</div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function FilterChip({ icon, active, onClick, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border transition-colors ${
        active
          ? "bg-brand-red text-white border-brand-red"
          : "bg-white text-ink border-sand hover:border-brand-red"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}
